import { useState } from "react";
import ImageIcon from "../../../assets/icons/ImageIcon";
import { InputBaseParams } from "../../forms/Form";
import FileInput from "../../forms/inputs/FileInput";
import ThumbsUp from "../../../assets/icons/ThumbsUp";
import InlineTabSelector from "./InlineTabSelector";

enum ImageType {
  Profile = "Profile",
  Banner = "Banner"
}

export type ProfileImageFieldValues = {
  profilePicture?: Blob[];
  bannerPicture?: Blob[];
};

const ProfileImageUploader = ({
  rules,
  className,
  imageUpToDate = false,
  onAfterChange
}: Omit<InputBaseParams, "name"> & {
  imageUpToDate?: boolean;
  className?: string;
  onAfterChange?: () => void;
}) => {
  const [selected, setSelected] = useState<string | undefined>(
    ImageType.Profile
  );
  const [success, setSuccess] = useState<ImageType[]>([]);

  const update = (type: ImageType) => {
    setSelected(undefined);
    setSuccess([...success.filter(t => t !== type), type]);
    if (onAfterChange) {
      // Defer slightly so form state updates prior to submission hooks
      setTimeout(() => onAfterChange(), 0);
    }
  };

  return (
    <section className={"w-full " + className}>
      <div className="mb-3 flex w-full items-center gap-4">
        <p className="flex gap-[5px]">
          <ImageIcon className="stroke-gray-700" />
          Update Photos
        </p>

        <InlineTabSelector
          value={selected}
          onChange={value => setSelected(value)}
          tabs={[ImageType.Profile, ImageType.Banner]}
          toggle
        />
      </div>

      {success.length > 0 && (
        <div className="mb-3">
          {success.sort().map((type, index) => (
            <div
              key={index}
              className="mb-2 flex items-center justify-center gap-1 rounded-full border-[1px] border-ctb-grey-200 bg-ctb-grey-400 py-2"
            >
              <ThumbsUp className="stroke-ctb-blue-400" />
              <p className="text-[13px]">
                {type} photo {imageUpToDate ? "has been" : "will be"} updated
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Use two different file inputs so we get two images on the form submit */}
      <FileInput
        name="profilePicture"
        rules={rules}
        accept=".jpg,.png,.jpeg"
        handleChange={() => update(ImageType.Profile)}
        className={selected === ImageType.Profile ? "" : "hidden"}
        cropImage={{
          title: "New Profile",
          cropSize: {
            width: 200,
            height: 200
          },
          previewSize: {
            width: 230,
            height: 290
          },
          minZoom: 50,
          maxZoom: 300
        }}
        maxSize={800000}
      />
      <FileInput
        name="bannerPicture"
        rules={rules}
        accept=".jpg,.png,.jpeg"
        handleChange={() => update(ImageType.Banner)}
        className={selected === ImageType.Banner ? "" : "hidden"}
        maxSize={800000}
        cropImage={{
          title: "New Profile",
          cropSize: {
            width: 600,
            height: 150
          },
          previewSize: {
            width: 630,
            height: 190
          },
          minZoom: 50,
          maxZoom: 300
        }}
      />
    </section>
  );
};

export default ProfileImageUploader;
